import React, { useState } from 'react';
import {
    Modal,
    Button,
    Form,
    Alert,
    Spinner,
    ToggleButtonGroup,
    ToggleButton,
} from 'react-bootstrap';
import PropTypes from 'prop-types';

import { REPORT_REASONS } from '@/shared/services/reportApi';
import { reportReply } from '@/shared/services/replyApi';

const ReplyReportModal = ({
    show,
    onHide,
    replyId,
    axiosInstance,
    onReportSuccess,
}) => {
    const [selectedReason, setSelectedReason] = useState('');
    const [comments, setComments] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const isOtherSelected = selectedReason === 'other';
    const isCommentsRequired = isOtherSelected;

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!selectedReason) {
            setError('Please select a reason for your report.');
            return;
        }

        if (isCommentsRequired && !comments.trim()) {
            setError('Please provide details for your report.');
            return;
        }

        setIsSubmitting(true);
        setError(null);

        try {
            await reportReply(axiosInstance, replyId, {
                reason: selectedReason,
                comments: comments.trim() || undefined,
            });

            setSuccess(true);

            // Call success callback if provided with the reason
            if (onReportSuccess) {
                onReportSuccess(selectedReason);
            }

            // Auto-close after showing success message
            setTimeout(() => {
                handleClose();
            }, 2000);
        } catch (err) {
            const errorMessage =
                err.response?.data?.message ||
                'Failed to submit report. Please try again.';
            setError(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        // Reset state when closing
        setSelectedReason('');
        setComments('');
        setError(null);
        setSuccess(false);
        setIsSubmitting(false);
        onHide();
    };

    return (
        <Modal
            show={show}
            onHide={handleClose}
            centered
            aria-labelledby="report-modal-title"
        >
            <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title
                    id="report-modal-title"
                    className="text-primary fw-bold"
                >
                    Report Reply
                </Modal.Title>
            </Modal.Header>

            <Form onSubmit={handleSubmit}>
                <Modal.Body className="pt-3">
                    {success ? (
                        <Alert variant="success" className="mb-0">
                            <i className="bi bi-check-circle me-2" />
                            Thank you for your report. Our team will review it.
                        </Alert>
                    ) : (
                        <>
                            {error && (
                                <Alert variant="danger" className="mb-3">
                                    {error}
                                </Alert>
                            )}

                            <p className="text-muted mb-3">
                                Help us understand what&apos;s wrong with this
                                reply. Select the reason that best describes the
                                issue.
                            </p>

                            <ToggleButtonGroup
                                type="radio"
                                name="reportReason"
                                value={selectedReason}
                                onChange={(val) => {
                                    setSelectedReason(val);
                                    if (error) setError(null);
                                }}
                                className="d-flex flex-column gap-2"
                            >
                                {REPORT_REASONS.map((reason) => (
                                    <ToggleButton
                                        key={reason.value}
                                        id={`reason-${reason.value}`}
                                        value={reason.value}
                                        variant={
                                            selectedReason === reason.value
                                                ? 'primary'
                                                : 'outline-primary'
                                        }
                                        disabled={isSubmitting}
                                        className="text-start rounded-pill"
                                    >
                                        {reason.label}
                                    </ToggleButton>
                                ))}
                            </ToggleButtonGroup>

                            <Form.Group className="mt-3">
                                <Form.Label className="text-primary fw-medium">
                                    Additional comments
                                    {isCommentsRequired ? (
                                        <span className="text-danger"> *</span>
                                    ) : (
                                        <span className="text-muted fw-normal">
                                            {' '}
                                            (optional)
                                        </span>
                                    )}
                                </Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    placeholder={
                                        isCommentsRequired
                                            ? 'Please describe the issue...'
                                            : 'Provide any additional details that might help us review this report...'
                                    }
                                    value={comments}
                                    onChange={(e) =>
                                        setComments(e.target.value)
                                    }
                                    disabled={isSubmitting}
                                />
                            </Form.Group>
                        </>
                    )}
                </Modal.Body>

                {!success && (
                    <Modal.Footer className="border-0 pt-0">
                        <Button
                            variant="outline-primary"
                            onClick={handleClose}
                            disabled={isSubmitting}
                            className="px-4 rounded-pill"
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            type="submit"
                            disabled={isSubmitting || !selectedReason}
                            className="px-4 rounded-pill"
                        >
                            {isSubmitting ? (
                                <>
                                    <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                        className="me-2"
                                    />
                                    Submitting...
                                </>
                            ) : (
                                'Submit Report'
                            )}
                        </Button>
                    </Modal.Footer>
                )}
            </Form>
        </Modal>
    );
};

ReplyReportModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    replyId: PropTypes.string.isRequired,
    axiosInstance: PropTypes.object.isRequired,
    onReportSuccess: PropTypes.func,
};

export default ReplyReportModal;
